import type { FormValues } from "@/widgets/classic-form/form";
import { useFormAPI } from "@/widgets/classic-form/form";
import type { HierarchicalFieldGroup, Hierarchies } from "@/entities/form-metadata";
import { useMetadataParamsContext } from "@/entities/form-metadata";
import type { IdHierarchy } from "@/entities/place";
import { useCategoryPlaceDataQuery } from "@/entities/place";
import type { GridSpan } from "@/shared/components/Grid";
import { Grid } from "@/shared/components/Grid";
import type { PrefixObjectAccessor } from "@/shared/forms";
import { HierarchyInitializationProvider } from "./HierarchyInitializationContext";
import { HierarchyLevelItem } from "./HierarchyLevelItem";

interface HierarchyInstanceProps {
    hierarchy: Hierarchies;
}

const HierarchyInstance = ({ hierarchy }: HierarchyInstanceProps) => {
    const { getFieldValue } = useFormAPI();

    const baseFormPath = `events.${hierarchy.relation}${hierarchy.eventType}.instances.0`;
    const gpidFieldName = `${baseFormPath}.gpid` as PrefixObjectAccessor<FormValues, []>;
    const gpid = getFieldValue(gpidFieldName);

    const { data } = useCategoryPlaceDataQuery(gpid as string | undefined);

    const locationLevels: IdHierarchy = data?.[0]?.IdHierarchy ?? [];

    return (
        <HierarchyInitializationProvider>
            {hierarchy.fields.map((field) => (
                <Grid.Field
                    key={field.label}
                    spanDesktop={(field.features?.gridColumnSpan as GridSpan) ?? 12}
                    spanTablet={(field.features?.gridColumnSpan as GridSpan) ?? 12}
                    spanMobile={12}
                >
                    <HierarchyLevelItem
                        field={field}
                        hierarchy={hierarchy}
                        locationLevels={locationLevels}
                        testId={baseFormPath}
                    />
                </Grid.Field>
            ))}
        </HierarchyInitializationProvider>
    );
};

interface HierarchicalGroupProps {
    fieldGroup: HierarchicalFieldGroup;
}
export const HierarchicalGroup = ({ fieldGroup }: HierarchicalGroupProps) => {
    const metadata = useMetadataParamsContext();
    const { hierarchies } = fieldGroup;

    if (metadata.formType !== "contentbased") return null;

    const hierarchyInstances = hierarchies.map((hierarchy) => (
        <HierarchyInstance key={`${hierarchy.relation}:${hierarchy.assertion}`} hierarchy={hierarchy} />
    ));
    return <Grid.Container>{hierarchyInstances}</Grid.Container>;
};
